import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import Modal from "../../../shared/components/Modal";
import PaginationToolbar from "../../../shared/components/PaginationToolbar";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { catalogConfig, normalizarEstado } from "../../../dominio/catalogos";
import Pacientes from "./Pacientes";
import TiposCita from "./TiposCita";

const tabInicial = (tab) => (catalogConfig[tab] || tab === "pacientes" || tab === "tiposcita" ? tab : "pacientes");

export default function Administrar() {
  const { apiFetch } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabActiva = tabInicial(searchParams.get("tab"));
  const [catalogos, setCatalogos] = useState({ fonemas: [], problemas: [] });
  const [form, setForm] = useState(catalogConfig.fonemas.emptyForm);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmEstado, setConfirmEstado] = useState({ visible: false, item: null });
  const [mensaje, setMensaje] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const pacientesRef = useRef(null);
  const tiposCitaRef = useRef(null);

  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const config = catalogConfig[tabActiva];
  const esCatalogo = Boolean(config);
  const registros = useMemo(() => (esCatalogo ? catalogos[tabActiva] || [] : []), [catalogos, esCatalogo, tabActiva]);

  const listarCatalogo = useCallback(async (tipo) => {
    const actual = catalogConfig[tipo];
    setCargandoCatalogo(true);

    try {
      const data = await apiFetch(`${actual.endpoint}?action=list`, "GET", null, {
        silent: tipo === "problemas"
      });
      setCatalogos((prev) => ({
        ...prev,
        [tipo]: data || []
      }));
    } finally {
      setCargandoCatalogo(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (!esCatalogo) return;
    listarCatalogo(tabActiva);
  }, [esCatalogo, listarCatalogo, refreshKey, tabActiva]);

  useEffect(() => {
    if (!esCatalogo) return;
    setForm(config.emptyForm);
    setShowFormModal(false);
    setConfirmEstado({ visible: false, item: null });
    setBusqueda("");
    setPaginaActual(1);
  }, [config, esCatalogo]);

  const registrosFiltrados = useMemo(() => {
    if (!esCatalogo) return [];

    const textoBusqueda = busqueda.toLowerCase();
    return registros.filter((item) => {
      const activo = normalizarEstado(item[config.stateKey] ?? item.Estado ?? item.Activo) === 1;
      const coincideEstado = mostrarInactivos || activo;
      const coincideTexto = config.campos.some((campo) =>
        String(item[campo.name] || "").toLowerCase().includes(textoBusqueda)
      );

      return coincideEstado && coincideTexto;
    });
  }, [busqueda, config, esCatalogo, mostrarInactivos, registros]);

  const totalPaginas = Math.ceil(registrosFiltrados.length / registrosPorPagina);
  const indiceInicio = (paginaActual - 1) * registrosPorPagina;
  const registrosPaginados = registrosFiltrados.slice(indiceInicio, indiceInicio + registrosPorPagina);

  const abrirModalAgregar = () => {
    if (!esCatalogo) return;
    setForm(config.emptyForm);
    setMensaje(null);
    setShowFormModal(true);
  };

  const abrirAgregarActivo = () => {
    if (tabActiva === "pacientes") {
      pacientesRef.current?.abrirModalAgregar();
      return;
    }

    if (tabActiva === "tiposcita") {
      tiposCitaRef.current?.abrirModalAgregar();
      return;
    }

    abrirModalAgregar();
  };

  const getAgregarLabel = () => {
    if (tabActiva === "pacientes") return "Agregar paciente";
    if (tabActiva === "tiposcita") return "Agregar tipo";
    return `Agregar ${config.singular}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((actual) => ({ ...actual, [name]: value }));
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!esCatalogo) return;
    const resp = await apiFetch(`${config.endpoint}?action=add`, "POST", form);

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo guardar el registro." });
      return;
    }

    setMensaje({
      tipo: "success",
      texto: `${config.singular} agregado correctamente.`
    });
    setShowFormModal(false);
    setForm(config.emptyForm);
    listarCatalogo(tabActiva);
  };

  const pedirCambioEstado = (item) => {
    setConfirmEstado({ visible: true, item });
  };

  const cancelarCambioEstado = () => {
    setConfirmEstado({ visible: false, item: null });
  };

  const cambiarEstado = async () => {
    if (!confirmEstado.item || !esCatalogo) return;

    const item = confirmEstado.item;
    const estadoActual = normalizarEstado(item[config.stateKey] ?? item.Estado ?? item.Activo);
    const nuevoEstado = estadoActual === 1 ? 0 : 1;

    const resp = await apiFetch(`${config.endpoint}?action=update`, "PUT", {
      ...item,
      [config.stateKey]: nuevoEstado
    });

    if (resp?.ok === false) {
      setMensaje({ tipo: "danger", texto: resp?.error || "No se pudo cambiar el estado." });
      return;
    }

    setMensaje({
      tipo: "success",
      texto: `${config.singular} ${nuevoEstado === 1 ? "activado" : "inactivado"} correctamente.`
    });
    cancelarCambioEstado();
    listarCatalogo(tabActiva);
  };

  const cambiarTab = (tab) => {
    if (tab === tabActiva) {
      setRefreshKey((actual) => actual + 1);
      return;
    }

    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="container-fluid mt-3 px-3 px-md-4">
      <h2>Administracion</h2>

      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div className="admin-tabs" role="tablist" aria-label="Catalogos administrables">
          <button
            type="button"
            className={`admin-tab ${tabActiva === "pacientes" ? "active" : ""}`}
            onClick={() => cambiarTab("pacientes")}
          >
            Pacientes
          </button>
          <button
            type="button"
            className={`admin-tab ${tabActiva === "tiposcita" ? "active" : ""}`}
            onClick={() => cambiarTab("tiposcita")}
          >
            Tipos de Cita
          </button>
          <button
            type="button"
            className={`admin-tab ${tabActiva === "fonemas" ? "active" : ""}`}
            onClick={() => cambiarTab("fonemas")}
          >
            Fonemas
          </button>
          <button
            type="button"
            className={`admin-tab ${tabActiva === "problemas" ? "active" : ""}`}
            onClick={() => cambiarTab("problemas")}
          >
            Problemas de comunicacion
          </button>
        </div>
        <button type="button" className="btn btn-success btn-sm" onClick={abrirAgregarActivo}>
          {getAgregarLabel()}
        </button>
      </div>

      {tabActiva === "pacientes" && <Pacientes ref={pacientesRef} embedded refreshKey={refreshKey} />}
      {tabActiva === "tiposcita" && <TiposCita ref={tiposCitaRef} embedded refreshKey={refreshKey} />}

      {esCatalogo && (
        <>

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      <div className="card mb-3">
        <div className="card-body admin-filter-bar">
          <input
            type="text"
            className="form-control"
            placeholder={`Buscar en ${config.titulo.toLowerCase()}...`}
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPaginaActual(1);
            }}
          />
          <label className="form-check admin-inactive-toggle">
            <input
              className="form-check-input"
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => {
                setMostrarInactivos(e.target.checked);
                setPaginaActual(1);
              }}
            />
            <span className="form-check-label">Mostrar inactivos</span>
          </label>
        </div>
      </div>

      <div className="card edge-to-edge-table-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-sm align-middle mobile-compact-table admin-catalog-table">
              <thead className="table-dark">
                <tr>
                  {config.campos.map((campo) => (
                    <th key={campo.name}>{campo.label}</th>
                  ))}
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cargandoCatalogo && (
                  <tr>
                    <td colSpan={config.campos.length + 2} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Cargando registros...
                    </td>
                  </tr>
                )}
                {!cargandoCatalogo && registrosPaginados.map((item) => {
                  const activo = normalizarEstado(item[config.stateKey] ?? item.Estado ?? item.Activo) === 1;
                  return (
                    <tr key={item[config.idKey]}>
                      {config.campos.map((campo) => (
                        <td key={campo.name} className="text-truncate" title={item[campo.name]}>
                          {item[campo.name] || "N/A"}
                        </td>
                      ))}
                      <td>
                        <span className={`badge bg-${activo ? "success" : "secondary"}`}>
                          {activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className={`btn btn-${activo ? "warning" : "success"}`}
                            type="button"
                            title={activo ? "Inactivar" : "Activar"}
                            onClick={() => pedirCambioEstado(item)}
                          >
                            {activo ? "Inactivar" : "Activar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!cargandoCatalogo && registros.length === 0 && (
                  <tr>
                    <td colSpan={config.campos.length + 2} className="text-center">No hay registros.</td>
                  </tr>
                )}
                {!cargandoCatalogo && registros.length > 0 && registrosFiltrados.length === 0 && (
                  <tr>
                    <td colSpan={config.campos.length + 2} className="text-center">No se encontraron resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PaginationToolbar
        page={paginaActual}
        totalPages={totalPaginas}
        pageSize={registrosPorPagina}
        disabled={cargandoCatalogo}
        onPageChange={setPaginaActual}
        onPageSizeChange={(size) => {
          setRegistrosPorPagina(size);
          setPaginaActual(1);
        }}
      />

      {showFormModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Agregar {config.singular}</h5>
                <button type="button" className="btn-close" onClick={() => setShowFormModal(false)}></button>
              </div>
              <div className="modal-body">
                <form className="row g-3" onSubmit={guardar}>
                  {config.campos.map((campo) => (
                    <div className={campo.type === "textarea" ? "col-12" : "col-12 col-md-6"} key={campo.name}>
                      <label className="form-label">{campo.label}</label>
                      {campo.type === "textarea" ? (
                        <textarea
                          className="form-control"
                          name={campo.name}
                          rows="3"
                          value={form[campo.name] || ""}
                          onChange={handleChange}
                          required={campo.required}
                        ></textarea>
                      ) : (
                        <input
                          className="form-control"
                          name={campo.name}
                          value={form[campo.name] || ""}
                          onChange={handleChange}
                          required={campo.required}
                        />
                      )}
                    </div>
                  ))}
                  <div className="col-12 col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="form-select" name={config.stateKey} value={form[config.stateKey]} onChange={handleChange}>
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFormModal(false)}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-success btn-sm">
                      Guardar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmEstado.visible && (
        <Modal
          title="Confirmar cambio de estado"
          onClose={cancelarCambioEstado}
          footer={
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={cancelarCambioEstado}>
                Cancelar
              </button>
              <button type="button" className="btn btn-success btn-sm" onClick={cambiarEstado}>
                Confirmar
              </button>
            </>
          }
        >
          <p className="mb-0">
            Deseas {normalizarEstado(confirmEstado.item?.[config.stateKey] ?? confirmEstado.item?.Estado ?? confirmEstado.item?.Activo) === 1 ? "inactivar" : "activar"}{" "}
            <strong>{confirmEstado.item?.Nombre || confirmEstado.item?.Simbolo}</strong>?
          </p>
        </Modal>
      )}

        </>
      )}
    </div>
  );
}
